import useJwt from '@/auth/jwt/useJwt'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const EXPIRATION_TIME = 60 * 60 * 1000

const entityConfig = {
  account: {
    storageKey: 'accountNames',
    timestampKey: 'accountNamesTimestamp',
    fetchNames: ids => useJwt.accountNamesById(ids),
  },
  widget: {
    storageKey: 'widgetNames',
    timestampKey: 'widgetNamesTimestamp',
    fetchNames: ids => useJwt.widgetNamesById(ids),
  },
  banner: {
    storageKey: 'bannerNames',
    timestampKey: 'bannerNamesTimestamp',
    fetchNames: ids => useJwt.bannerNamesById(ids),
  },
}

const storedNames = type => JSON.parse(localStorage.getItem(entityConfig[type].storageKey)) || []

const isExpired = type => {
  const lastSavedTime = localStorage.getItem(entityConfig[type].timestampKey)
  const timestamp = Number(lastSavedTime)

  return !lastSavedTime || Number.isNaN(timestamp) || Date.now() - timestamp > EXPIRATION_TIME
}

const nameById = (state, type, id) => {
  if (isExpired(type)) {
    return id
  }

  const entity = state[type].find(item => item.id === id)
  return entity ? entity.name : id
}

export default {
  namespaced: true,
  state: {
    account: storedNames('account'),
    widget: storedNames('widget'),
    banner: storedNames('banner'),
  },
  getters: {
    accountNameByIdFromStore: state => id => nameById(state, 'account', id),
    widgetNameByIdFromStore: state => id => nameById(state, 'widget', id),
    bannerNameByIdFromStore: state => id => nameById(state, 'banner', id),
  },
  mutations: {
    RESET_ENTITY_NAMES(state, type) {
      state[type] = []
      localStorage.removeItem(entityConfig[type].storageKey)
      localStorage.removeItem(entityConfig[type].timestampKey)
    },
    ADD_ENTITY_NAMES(state, { type, names }) {
      const currentIds = new Set(state[type].map(item => item.id))

      names.forEach(item => {
        if (!currentIds.has(item.id)) {
          state[type].push(item)
        }
      })

      localStorage.setItem(entityConfig[type].storageKey, JSON.stringify(state[type]))
      localStorage.setItem(entityConfig[type].timestampKey, Date.now().toString())
    },
  },
  actions: {
    async fetchEntityNamesById({ commit, state }, { type, ids }) {
      const uniqueIds = [...new Set(ids.filter(Boolean))]
      if (!uniqueIds.length) {
        return
      }

      let idsToFetch = uniqueIds
      if (isExpired(type)) {
        commit('RESET_ENTITY_NAMES', type)
      } else {
        idsToFetch = uniqueIds.filter(id => !state[type].some(item => item.id === id))
        if (!idsToFetch.length) {
          return
        }
      }

      try {
        const response = await entityConfig[type].fetchNames(idsToFetch)
        commit('ADD_ENTITY_NAMES', {
          type,
          names: Array.isArray(response.data) ? response.data : [],
        })
      } catch (error) {
        if (error.response && error.response.status === 422 && error.response.data.errors.length) {
          error.response.data.errors.forEach(err => {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Error',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: err.detail,
              },
            })
          })
        }
      }
    },
    fetchAccountNamesById({ dispatch }, ids) {
      return dispatch('fetchEntityNamesById', { type: 'account', ids })
    },
    fetchWidgetNamesById({ dispatch }, ids) {
      return dispatch('fetchEntityNamesById', { type: 'widget', ids })
    },
    fetchBannerNamesById({ dispatch }, ids) {
      return dispatch('fetchEntityNamesById', { type: 'banner', ids })
    },
  },
}
